/**
 * Email Payment Card Component
 * 
 * Combined component for email entry and "Add Payment Method" button.
 * Follows the UX pattern from the original web-portal where users enter
 * their email and click "Add Payment Method" to start the payment flow.
 * 
 * @module pages/booking/components
 */

import { useState } from 'react';

export interface EmailPaymentCardProps {
  /** User's email address */
  email: string;
  /** Email change handler */
  onEmailChange: (email: string) => void;
  /** Callback when "Add Payment Method" is clicked */
  onAddPaymentMethod: () => void;
  /** Whether the form is currently processing */
  isProcessing?: boolean;
  /** The button's label — "Continue" when the club collects at the counter and no card follows. */
  actionLabel?: string;
}

/**
 * Email Payment Card Component
 *
 * Single screen with:
 * - Email input
 * - "Add Payment Method" button
 *
 * Button is disabled until email is valid.
 */
export function EmailPaymentCard({
  email,
  onEmailChange,
  onAddPaymentMethod,
  isProcessing = false,
  actionLabel = 'Add Payment Method',
}: EmailPaymentCardProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Derive loading state: local click feedback OR parent processing
  // When parent's isProcessing catches up, it takes over.
  // When isProcessing goes false (e.g. on error), isSubmitting is stale but
  // isProcessing=false means we're no longer loading — so the OR still works.
  // We reset isSubmitting on every render where isProcessing is false.
  if (!isProcessing && isSubmitting) {
    setIsSubmitting(false);
  }
  const isLoading = isProcessing || isSubmitting;

  // Email validation
  const isEmailValid = (emailToValidate: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailToValidate);
  };

  // Button enabled logic
  const isButtonEnabled =
    !isLoading &&
    isEmailValid(email);

  // Handle click with immediate local loading state
  const handleClick = () => {
    setIsSubmitting(true);
    onAddPaymentMethod();
  };

  return (
    <div>
      {/* Email Input */}
      <h3 
      className='text-xl font-semibold text-fg-secondary mb-6'
      data-testid="checkout-payment-details-title"
      >Payment Details</h3>
      <div className="mb-4">
        <label 
          htmlFor="email" 
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          disabled={isProcessing}
          placeholder="you@example.com"
          className="w-full px-4 py-3 bg-theme-50 border border-theme-600 rounded-2xl focus:ring-2 focus:ring-theme-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 text-base"
          data-testid="checkout-email-input"
        />
        {email && !isEmailValid(email) && (
          <p 
          className="mt-2 text-sm text-red-600"
          data-testid="checkout-invalid-email-error"
          >
            Please enter a valid email address
          </p>
        )}
      </div>

      {/* Add Payment Method Button */}
      <button
        onClick={handleClick}
        disabled={!isButtonEnabled}
        className={`
          w-full px-6 py-3 rounded-full font-medium text-base
          transition-all duration-200
          ${isButtonEnabled 
            ? 'bg-theme-600 text-white text-interactive:bg-theme-700' 
            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }
        `}
        data-testid="checkout-add-payment-method-btn"
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg 
              className="animate-spin h-5 w-5" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24"
            >
              <circle 
                className="opacity-25" 
                cx="12" 
                cy="12" 
                r="10" 
                stroke="currentColor" 
                strokeWidth="4"
              />
              <path 
                className="opacity-75" 
                fill="currentColor" 
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Processing...
          </span>
        ) : (
          actionLabel
        )}
      </button>
    </div>
  );
}
